import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Sparkles, Loader2, CheckCircle, XCircle } from 'lucide-react';
import apiClient from '../../services/api/client';

// Helper to check if error is a retryable network error
const isRetryableNetworkError = (error) => {
  if (error.response) return false; // Has response = not a network error
  const message = error.message || '';
  return (
    message.includes('Network Error') ||
    message.includes('ERR_NETWORK') ||
    message.includes('ERR_CONNECTION') ||
    message.includes('ERR_INTERNET_DISCONNECTED') ||
    message.includes('ERR_NETWORK_CHANGED')
  );
};

// Simple delay helper
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const MagicLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check for token immediately - redirect before rendering if missing
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error, redirecting
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Note: If no token, we render <Navigate> directly below instead of using useEffect
  // This ensures synchronous redirect during the render phase (no loading page shown)

  const verifyToken = useCallback(async (isMounted, attempt = 0) => {
    if (!token) {
      // No token - redirect is handled by <Navigate> component
      return { success: false };
    }

    try {
      // First, verify the token to get user info (GET request)
      const verifyResponse = await apiClient.get('/auth/magic-link/verify', {
        params: { token }
      });

      // Check if component is still mounted before updating state
      if (!isMounted.current) return;

      if (verifyResponse.data?.valid) {
        setUserInfo({
          email: verifyResponse.data.email,
          firstName: verifyResponse.data.first_name
        });

        // Then, complete the login to get access token (POST request)
        const loginResponse = await apiClient.post('/auth/magic-link/verify', null, {
          params: { token }
        });

        // Check if component is still mounted before updating state
        if (!isMounted.current) return;

        if (loginResponse.data?.access_token) {
          // Store the token in localStorage (same as regular login)
          localStorage.setItem('access_token', loginResponse.data.access_token);
          setStatus('success');
          return { success: true };
        } else {
          // No access token returned - show error
          setStatus('error');
          setError('La connexion a échoué. Veuillez réessayer.');
          return { success: false };
        }
      } else {
        // Token is not valid
        setStatus('error');
        setError(verifyResponse.data?.message || 'Ce lien n\'est plus valide.');
        return { success: false };
      }
    } catch (err) {
      // Check if component is still mounted before updating state
      if (!isMounted.current) return { success: false };

      // Handle auth redirect from API client interceptor (token expired during request)
      if (err.isAuthRedirect) {
        // API client is handling redirect, just clean up
        return { success: false };
      }

      // Retry on network errors (up to MAX_RETRIES times)
      if (isRetryableNetworkError(err) && attempt < MAX_RETRIES) {
        setRetryCount(attempt + 1);
        // Exponential backoff: 1s, 2s, 4s
        await delay(1000 * Math.pow(2, attempt));
        if (isMounted.current) {
          return verifyToken(isMounted, attempt + 1);
        }
        return { success: false };
      }

      setStatus('error');
      if (err.response?.status === 400) {
        // Backend returns 400 for invalid or expired tokens
        setError(err.response.data?.detail || 'Ce lien n\'est plus valide.');
      } else if (err.response?.status === 404) {
        setError('Ce lien n\'existe pas ou a déjà été utilisé');
      } else if (err.response?.status === 410) {
        setError('Ce lien a expiré. Veuillez en demander un nouveau.');
      } else if (err.response?.status === 403) {
        setError(err.response.data?.detail || 'Accès non autorisé');
      } else if (err.response?.status === 401) {
        // Silently redirect to login for auth errors
        window.location.href = '/login';
        return { success: false };
      } else if (!err.response) {
        // Network error - no response from server (after retries exhausted)
        setError('Erreur de connexion. Vérifiez votre connexion internet et réessayez.');
      } else {
        setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
      }
      return { success: false };
    }
  }, [token]);

  useEffect(() => {
    // Don't run verification if no token (<Navigate> handles redirect)
    if (!token) return;

    const isMounted = { current: true };
    const statusRef = { current: 'verifying' };
    let redirectTimeout = null;
    let verificationTimeout = null;

    const runVerification = async () => {
      const result = await verifyToken(isMounted);
      if (result?.success && isMounted.current) {
        statusRef.current = 'success';
        // Redirect after a short delay to show success message
        redirectTimeout = setTimeout(() => {
          if (isMounted.current) {
            window.location.href = '/dashboard'; // Full reload to ensure proper auth state
          }
        }, 2000);
      } else if (isMounted.current) {
        statusRef.current = 'done';
      }
    };

    runVerification();

    // Timeout to prevent infinite loading - show error after 30 seconds
    verificationTimeout = setTimeout(() => {
      if (isMounted.current && statusRef.current === 'verifying') {
        setStatus('error');
        setError('La vérification a pris trop de temps. Veuillez réessayer.');
      }
    }, 30000);

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted.current = false;
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
      if (verificationTimeout) {
        clearTimeout(verificationTimeout);
      }
    };
  }, [verifyToken, token]);

  // Immediate redirect when no token - uses Navigate component for synchronous routing
  // This ensures the user never sees the MagicLogin page, they go directly to /login
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="SYLink Pentest"
            className="h-16 mx-auto"
          />
        </div>

        {/* Card */}
        <div
          className="bg-[#0d0d0d] border border-gray-800 rounded-2xl p-8 shadow-2xl"
          style={{ boxShadow: '0 0 50px rgba(0, 200, 255, 0.1)' }}
        >
          {/* Verifying State */}
          {status === 'verifying' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-cyan-500/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                {retryCount > 0 ? 'Nouvelle tentative...' : 'Vérification en cours...'}
              </h2>
              <p className="text-gray-400 text-sm">
                {retryCount > 0
                  ? `Tentative ${retryCount + 1}/${MAX_RETRIES + 1} - Reconnexion en cours`
                  : 'Nous vérifions votre lien de connexion'}
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Connexion réussie !
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                {userInfo?.firstName ? (
                  <>Bienvenue, <span className="text-white font-medium">{userInfo.firstName}</span> !</>
                ) : (
                  'Vous allez être redirigé vers votre tableau de bord...'
                )}
              </p>
              <div className="flex items-center justify-center gap-2 text-cyan-400 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>Redirection automatique...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Lien invalide
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 rounded-xl transition-colors"
                >
                  Retour à la connexion
                </button>
                <p className="text-gray-500 text-xs">
                  Vous pouvez demander un nouveau lien magique depuis la page de connexion.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-xs">
            Lien magique - Connexion sécurisée sans mot de passe
          </p>
        </div>
      </div>
    </div>
  );
};

export default MagicLogin;
